/**
 * A class representing a sticky Add to Cart (ATC) functionality.
 * It creates a sticky container with an image, title, quantity adjustment form, and a submit button.
 */

class StickyATC {
	/**
	 * Constructs a new sticky ATC instance.
	 *
	 * @param {string} title - The title of the product.
	 * @param {string} image - The URL of the product image.
	 */
	constructor(title, image) {
		this.title = title;
		this.image = image;

		/**
		 * Handle product form with shopify pattern.
		 */
		this.originalForm = document.querySelector("form[action='/cart/add']");
		this.form = this.originalForm;
		this.originalFormInput = this.form?.querySelector("input[name='quantity']");
		this.originalFormSubmit = this.form?.querySelector("[type='submit']");

		/**
		 * Create the sticky add to cart
		 */
		this.container = document.getElementById("opt7StickyATC");
		this.decreaseBtn = this.createDecreaseButton();
		this.increaseBtn = this.createIncreaseButton();
		this.input = this.createInput();
		this.cartIcon = this.createCartIcon();
		this.atc = this.createATCButton();
		this.stickyATC = this.createStickyContainer();

		/**
		 * Bind events to sticky add to cart functionalty
		 */
		this.increase = this.increaseValue.bind(this);
		this.decrease = this.decreaseValue.bind(this);
		this.setValue = this.setInputValue.bind(this);
		this.handleVisible = this.handleVisible.bind(this);
	}

	init() {
		if (this.container) {
			this.container.appendChild(this.stickyATC);

			window.addEventListener("load", this.handleVisible);
			window.addEventListener("resize", this.handleVisible);
			window.addEventListener("scroll", this.handleVisible);

			/**
			 * Event listener for the original form input.
			 * When the input value changes, it calls the `setValue` method for the sticky input.
			 */
			this.originalFormInput.addEventListener("input", (e) => {
				this.setValue(e.target.value);
			});
			window.addEventListener("product:quantity-update", (e) => {
				if (e.detail && typeof e.detail.quantity === "number") {
					this.setValue(e.detail.quantity);
				}
			});

			/**
			 * Event listener for the sticky quantity input field.
			 * When the input value changes, it updates the value of the original form input.
			 */
			this.container.querySelector("#opt7-sticky-qty").addEventListener("input", (e) => {
				this.originalFormInput.value = e.target.value;
			});

			/**
			 * Event listener for the submit button in the sticky ATC.
			 * It scrolls to the original form position, waits for 500ms, and then triggers the click event on the original Add to Cart button.
			 */
			this.container.querySelector(".opt7-sticky-atc-submit").addEventListener("click", () => {
				if (this.form) {
					const formPosition = this.form.getBoundingClientRect().top + window.pageYOffset;

					window.scrollTo({
						top: formPosition - 750,
						behavior: "smooth",
					});
				}

				setTimeout(() => {
					this.originalFormSubmit.click();
				}, 500);
			});

			/**
			 * Event listener for the decrement and increment buttons in the original form.
			 * It updates the sticky input value with the current quantity from the original form after a 150ms delay.
			 * It waits original quantity input for change
			 *
			 * @param {Event} event - The event object that triggered the function.
			 */

			// @TODO: Shopify için buraya tekrar bakılacak. (Sabit bir patern yok form değerinin değişme durumuna göre çözüm ara)
			this.form.querySelector("[name='plus']").addEventListener("click", () => {
				setTimeout(() => {
					this.input.value = this.form.querySelector("[name='quantity']").value;
				}, 150);
			});

			this.form.querySelector("[name='minus']").addEventListener("click", () => {
				setTimeout(() => {
					this.input.value = this.form.querySelector("[name='quantity']").value;
				}, 150);
			});
		}

		console.info(
			"%c Optimum7 Sticky ATC Initialized",
			"color: #FFF; background: #FF9800; line-height:1.5; font-size:16px; font-weight:bold; padding:6px 10px; font-family:sans-serif"
		);
	}

	/**
	 * Handles the visibility of the sticky ATC container.
	 * Adds or removes the "sticky" class to the container based on its position in the viewport.
	 */
	handleVisible() {
		const inViewport = this.isElementInViewport();

		if (!inViewport) {
			document.querySelector('[class^="HubLauncherWidget__FloatingWidget"]').style.bottom = "73px";
			this.container.classList.add("sticky");
		} else {
			document.querySelector('[class^="HubLauncherWidget__FloatingWidget"]').style.bottom = "40px";
			this.container.classList.remove("sticky");
		}
	}

	/**
	 * Checks if the element with the id "form-action-addToCart" is currently visible in the viewport.
	 *
	 * @returns {boolean} - Returns true if the element is in the viewport, false otherwise.
	 */
	isElementInViewport() {
		let el = this.form.querySelector("[type='submit']");

		if (el) {
			let rect = el.getBoundingClientRect();

			return (
				rect.top >= 0 &&
				rect.left >= 0 &&
				rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
				rect.right <= (window.innerWidth || document.documentElement.clientWidth)
			);
		}

		return false;
	}

	/**
	 * Creates a button for increasing the quantity in the sticky ATC.
	 *
	 * @returns {HTMLButtonElement} The created button element.
	 */
	createIncreaseButton() {
		const button = document.createElement("button");
		button.classList = "quantity-input-button quantity-input-plus";
		button.setAttribute("data-qty", "increase");
		button.innerHTML = "+";
		button.addEventListener("click", () => this.increaseValue());

		return button;
	}

	/**
	 * Creates a button for decreasing the quantity in the sticky ATC.
	 *
	 * @returns {HTMLButtonElement} The created button element.
	 */
	createDecreaseButton() {
		const button = document.createElement("button");
		button.classList = "quantity-input-button quantity-input-minus no-js-hidden";
		button.setAttribute("data-qty", "decrease");
		button.innerHTML = "-";
		button.addEventListener("click", () => this.decreaseValue());

		return button;
	}

	/**
	 * Creates an input field for the quantity in the sticky ATC.
	 *
	 * @returns {HTMLInputElement} The created input element.
	 */
	createInput() {
		const input = document.createElement("input");
		input.id = "opt7-sticky-qty";
		input.classList = "quantity-input-field";
		input.setAttribute("type", "number");
		input.value = 1;
		input.addEventListener("input", (e) => this.filterInputValue(e));
		input.addEventListener("change", () => this.fixInputValue());

		return input;
	}

	/**
	 * Creates a shopping cart icon element for the sticky ATC.
	 * @returns {HTMLParagraphElement} The created paragraph element containing the shopping cart icon.
	 */
	createCartIcon() {
		const cartIcon = document.createElement("p");
		cartIcon.classList = "sticky-atc-shopping-cart-icon";
		cartIcon.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 576 512">
                <path d="M0 24C0 10.7 10.7 0 24 0H69.5c22 0 41.5 12.8 50.6 32h411c26.3 0 45.5 25 38.6 50.4l-41 152.3c-8.5 31.4-37 53.3-69.5 53.3H170.7l5.4 28.5c2.2 11.3 12.1 19.5 23.6 19.5H488c13.3 0 24 10.7 24 24s-10.7 24-24 24H199.7c-34.6 0-64.3-24.6-70.7-58.5L77.4 54.5c-.7-3.8-4-6.5-7.9-6.5H24C10.7 48 0 37.3 0 24zM128 464a48 48 0 1 1 96 0 48 48 0 1 1 -96 0zm336-48a48 48 0 1 1 0 96 48 48 0 1 1 0-96z"/>
            </svg>
        `;

		return cartIcon;
	}

	/**
	 * Creates a button for adding the product to the shopping cart.
	 *
	 * @returns {HTMLButtonElement} The created button element with the class "opt7-sticky-atc-submit"
	 * and the inner HTML containing the text "Add to cart" and the shopping cart icon.
	 */
	createATCButton() {
		const atc = document.createElement("button");
		atc.classList = "opt7-sticky-atc-submit button button--primary";
		atc.innerHTML = `
            <span class="btn-txt">Add to cart</span>
            ${this.cartIcon.outerHTML.toString()}
        `;

		return atc;
	}

	/**
	 * Creates a sticky container element for the Add to Cart functionality.
	 * The container includes an image, title, form for quantity adjustment, and a submit button.
	 *
	 * @returns {HTMLDivElement} The created sticky container element.
	 */
	createStickyContainer() {
		const container = document.createElement("div");
		container.id = "opt7_sticky_atc_section";
		container.classList = "opt7-sticky-atc";
		container.innerHTML = `
            <div class="opt7-sticky-atc-inner">
							<div class="opt7-sticky-atc-img-section">
                <img class="opt7-sticky-atc-img" src="${this.image}" width="70" height="70" alt="${this.title}" />
							</div>
								<h2 class="opt7-sticky-atc-title">${this.title}</h2>
                <div class="opt7-sticky-atc-form">
                    <div class="opt7-sticky-qty quantity-input"></div>
                    <div class="opt7-sticky-atc-submit"></div>
                </div>
            </div>
        `;
		container.querySelector(".opt7-sticky-qty").appendChild(this.decreaseBtn);
		container.querySelector(".opt7-sticky-qty").appendChild(this.input);
		container.querySelector(".opt7-sticky-qty").appendChild(this.increaseBtn);
		container.querySelector(".opt7-sticky-atc-submit").appendChild(this.atc);
		return container;
	}

	/**
	 * Sets the value of the input field and ensures it is a valid number.
	 *
	 * @param {number} value - The new value for the input field.
	 */
	setInputValue(value) {
		this.input.value = value;
		this.fixInputValue();
	}

	/**
	 * Increases the value of the input field by 1.
	 *
	 * @param {number} [value] - This parameter is not used in this function. It is included for potential future enhancements.
	 */
	increaseValue(value) {
		this.input.value = parseInt(this.input.value) + 1;
		this.fixInputValue();
		this.originalFormInput.value = this.input.value;
	}

	/**
	 * Decreases the value of the input field by 1.
	 *
	 * @param {number} [value] - This parameter is not used in this function. It is included for potential future enhancements.
	 */
	decreaseValue(value) {
		this.input.value = Math.max(1, parseInt(this.input.value) - 1);
		this.fixInputValue();
		this.originalFormInput.value = this.input.value;
	}

	/**
	 * Fixes the value of the input field to ensure it is a valid number.
	 * If the input value is not a number or is an empty string, it sets the value to 0.
	 * If the input value starts with a zero and has a length greater than 1, it removes the leading zero.
	 */
	fixInputValue() {
		if (isNaN(this.input.value) || this.input.value === "") {
			this.input.value = 0;
		} else if (this.input.value.length > 1 && this.input.value[0] === "0") {
			this.input.value = this.input.value.slice(1);
		}
	}

	/**
	 * Filters the input value to ensure it only contains numeric characters.
	 * If the input value contains non-numeric characters, it replaces them with an empty string.
	 * Then, it calls the `fixInputValue` method to ensure the value is a valid number.
	 */
	filterInputValue() {
		const newValue = event.target.value.replace(/[^0-9]/g, "");
		if (newValue !== event.target.value) {
			event.target.value = newValue;
		}
		this.fixInputValue();
	}
}

window.StickyATC = StickyATC;
